import { useEffect } from "react";

import { useBreakpoint } from "use-breakpoint";
import { useLocation } from "react-router-dom";

import { cn } from "@/lib/utils";
import useChatStore from "@/stores/useChatStore";
import useGlobalStore from "@/stores/useGlobalStore";
import FooterContent from "@/components/FooterContent";
import { bubbleLoaderStyle } from "@/components/style";
import { BREAKPOINTS } from "@/constants/defaultValue";
import { REFERENCE_DELIMITER } from "@/constants/defaultValue";
import { useDynamicViewportHeight } from "@/hooks/useViewportPolyfill";

import Nav from "./components/Nav";
import Header from "./components/Header";
import ChatBox from "../../components/ChatBox";
import useChatLoader from "./components/ChatLoader";
import SuggestedQuestionTemplates from "./components/SuggestedQuestionTemplates";

function ChatLayout() {
  useChatLoader();
  const { breakpoint } = useBreakpoint(BREAKPOINTS);
  const footerHeight = useChatStore((state) => state.footerHeight);
  const showSuggestionPanel = useGlobalStore((state) => state.showSuggestionPanel);
  const hideSuggestionPanel = useGlobalStore((state) => state.hideSuggestionPanel);
  const isMobile = ["xs", "sm"].includes(breakpoint);
  const setQuestion = useChatStore((state) => state.setQuestion);
  const setReferenceMessage = useChatStore((state) => state.setReferenceMessage);
  const handleAskQuestionStream = useChatStore((state) => state.handleAskQuestionStream);
  const location = useLocation();
  const dynamicViewportHeight = useDynamicViewportHeight();

  useEffect(() => {
    if (isMobile) {
      useGlobalStore.setState({ navVisible: false });
    }
  }, [breakpoint, isMobile]);

  useEffect(() => {
    if (location.pathname === "/") {
      const data = localStorage.getItem("pendingChatData");
      if (data) {
        try {
          const { question, referenceMessage, reminderId } = JSON.parse(data);
          if (referenceMessage) {
            setQuestion(`${referenceMessage}${REFERENCE_DELIMITER}${question || ""}`);
          } else {
            setQuestion(question || "");
          }
          setReferenceMessage(referenceMessage || "");
          handleAskQuestionStream(reminderId);
          setQuestion("");
          setReferenceMessage("");
        } catch (e) {
          console.error(e);
        }
        localStorage.removeItem("pendingChatData");
      }
    }
  }, [location.pathname, setQuestion, setReferenceMessage, handleAskQuestionStream]);

  // Sử dụng height tự động cho mobile khi keyboard xuất hiện
  const containerHeight = isMobile ? "100dvh" : `${dynamicViewportHeight}px`;
  const chatBoxHeight = isMobile
    ? "calc(100dvh - 85px)" // Để browser tự động tính toán với dvh
    : `${dynamicViewportHeight - (footerHeight + 110)}px`;

  return (
    <div className={cn("flex flex-col overflow-hidden bg-[#F6F8FC] pb-[env(safe-area-inset-bottom)]", isMobile ? "mobile-viewport" : "")} style={!isMobile ? { height: containerHeight } : {}}>
      <style>{bubbleLoaderStyle}</style>
      <div className="flex flex-1 overflow-hidden">
        <Nav />

        <div className="w-full flex flex-col">
          <Header />

          <div className="flex flex-row flex-1 overflow-hidden">
            <div className={cn("transition-all duration-300 ease-in-out flex flex-col", !isMobile && showSuggestionPanel ? "w-[calc(100%-280px)]" : "w-full")}>
              <div
                className="mx-auto overflow-auto flex-1 overscroll-contain w-full"
                style={{
                  maxHeight: isMobile ? "calc(100dvh - 140px)" : chatBoxHeight,
                  background: "radial-gradient(200% 200% at 50% 50%, rgb(194 225 255 / 34%) 0%, rgba(230, 246, 244, 0) 20%)",
                }}
              >
                <ChatBox className="max-w-[820px]" isPopupContent={isMobile} isMobile={isMobile} />
              </div>
              <div className={cn("flex-shrink-0", isMobile && "sticky bottom-0 bg-[#F6F8FC] pt-2 keyboard-adjust")}>
                <FooterContent
                  isPopupContent={isMobile}
                  isMobile={isMobile}
                  className={"max-w-[820px] mx-auto px-4"}
                  inputClassName={cn("border-none shadow-[0_2px_8px_rgba(0,0,0,0.1)] focus-visible:ring-0 focus-visible:ring-offset-0", isMobile ? "pr-10" : "h-16 pl-5 pr-14")}
                />
              </div>
            </div>

            {showSuggestionPanel && <SuggestedQuestionTemplates onClose={hideSuggestionPanel} />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatLayout;
